<template>
  <form ref="profile_form" @submit.prevent="handleSubmit">
    <div class="row">
      <div class="col-12">
        <base-input
          :value="mail.from"
          :readOnly="true"
          :label="`${$t(`COMMON.MAIL_FROM`)}`"
        />
      </div>

      <div class="col-12">
        <base-input
          :label="`${$t(`COMMON.MAIL_TO`)} (*)`"
          :placeholder="$t('COMMON.SELECT_CONTACTS')"
        >
          <el-select
            v-model="mail.to"
            autocomplete="new-password"
            :placeholder="$t('COMMON.SELECT_CONTACTS')"
            :filterable="true"
            :multiple="true"
            remote
            :remote-method="getEstablishmentContacts"
            :loading="loadingContacts"
            :allow-create="true"
            default-first-option
            @focus="getEstablishmentContacts()"
          >
            <el-option
              v-for="contact in contactsList"
              :key="contact.id"
              :value="contact.email"
              :label="`${contact.firstname} ${contact.lastname} - ${contact.email}`"
            />
          </el-select>
        </base-input>
        <validation-error :errors="apiValidationErrors.to" />
      </div>

      <div class="col-12">
        <base-checkbox v-model="mail.sendMeCopy">
          <span class="form-control-label">
            {{ $t("COMMON.MAIL_SEND_ME_COPY") }}
          </span>
        </base-checkbox>
      </div>

      <div class="col-12">
        <base-input
          :label="$t('COMMON.MAIL_CC')"
          :placeholder="$t('COMMON.SELECT_CONTACTS')"
        >
          <el-select
            v-model="mail.cc"
            autocomplete="new-password"
            :placeholder="$t('COMMON.SELECT_CONTACTS')"
            :filterable="true"
            :multiple="true"
            remote
            :remote-method="getContacts"
            :loading="loadingContacts"
            :allow-create="true"
            default-first-option
          >
            <el-option
              v-for="contact in contactsList"
              :key="contact.id"
              :value="contact.email"
              :label="`${contact.firstname} ${contact.lastname} - ${contact.email}`"
            />
          </el-select>
        </base-input>
        <validation-error :errors="apiValidationErrors.cc" />
      </div>

      <div class="col-12">
        <base-input
          :label="$t('COMMON.MAIL_BCC')"
          :placeholder="$t('COMMON.SELECT_CONTACTS')"
        >
          <el-select
            v-model="mail.bcc"
            autocomplete="new-password"
            :placeholder="$t('COMMON.SELECT_CONTACTS')"
            :filterable="true"
            :multiple="true"
            remote
            :remote-method="getContacts"
            :loading="loadingContacts"
            :allow-create="true"
            default-first-option
          >
            <el-option
              v-for="contact in contactsList"
              :key="contact.id"
              :value="contact.email"
              :label="`${contact.firstname} ${contact.lastname} - ${contact.email}`"
            />
          </el-select>
        </base-input>
        <validation-error :errors="apiValidationErrors.bcc" />
      </div>

      <div class="col-12">
        <base-input
          :label="`${$t('SALES_ORDERS.INVOICING_DATE')} (*)`"
          :name="$t('SALES_ORDERS.INVOICING_DATE')"
        >
          <flat-picker
            v-model="mail.invoicing_date"
            :config="flatpickrConfig"
            class="form-control datepicker"
          />
        </base-input>
        <div
          v-if="!salesOrder.event_date"
          class="text-warning small"
        >
          {{ $t("SALES_ORDERS.INVOICING_DATE_FALLBACK_WARNING") }}
        </div>
        <validation-error :errors="apiValidationErrors.invoicing_date" />
      </div>

      <div class="col-12">
        <base-input
          v-model="mail.subject"
          :label="`${$t(`COMMON.MAIL_SUBJECT`)} (*)`"
        />
        <validation-error :errors="apiValidationErrors.subject" />
        <div
          v-if="invoiceNumberPending && !invoiceNumberPlaceholderRemoved"
          class="alert alert-info py-2 px-3 small"
        >
          {{
            $t("SALES_ORDERS.INVOICE_NUMBER_PLACEHOLDER_HINT", {
              token: invoiceNumberPlaceholder,
            })
          }}
        </div>
        <div
          v-if="invoiceNumberPlaceholderRemoved"
          class="alert alert-warning py-2 px-3 small"
        >
          {{
            $t("SALES_ORDERS.INVOICE_NUMBER_PLACEHOLDER_REMOVED", {
              token: invoiceNumberPlaceholder,
            })
          }}
        </div>
      </div>

      <div class="col-12">
        <h2>{{ $t("COMMON.MAIL_BODY") }}</h2>
        <html-editor v-model="mail.body" />
        <validation-error :errors="apiValidationErrors.body" />
      </div>
    </div>

    <div class="d-flex justify-content-start">
      <el-button
        :disabled="submitting || !canSubmit"
        @click="handleSubmit"
        type="success"
      >
        {{ submitLabel }}
      </el-button>
    </div>
  </form>
</template>

<script>
import { cloneDeep } from "lodash";
import moment from "moment-timezone";
import { Button, Option, Select } from "element-ui";
import flatPicker from "vue-flatpickr-component";
import { French } from "flatpickr/dist/l10n/fr.js";
import "flatpickr/dist/flatpickr.css";
import formMixin from "@/mixins/form-mixin";
import ValidationError from "@/components/ValidationError.vue";
import BaseInput from "@/components/Inputs/BaseInput.vue";
import HtmlEditor from "@/components/Inputs/HtmlEditor";

import { PRICING_TYPE_COUPONS } from "@/constants/pricingTypes";

const BUSINESS_TIMEZONE = "America/Toronto";

// Jeton renvoyé par l'API tant que la facture n'existe pas encore ; le
// dispatcher le remplace par le vrai numéro (F-xxxxx) au moment de l'envoi.
const INVOICE_NUMBER_PLACEHOLDER = "{{invoice_number}}";

export default {
  name: "sales-order-subsidized-invoice-mail-form",

  components: {
    BaseInput,
    ValidationError,
    HtmlEditor,
    flatPicker,
    [Select.name]: Select,
    [Option.name]: Option,
    [Button.name]: Button,
  },

  mixins: [formMixin],

  props: {
    salesOrder: {
      type: Object,
      required: true,
    },
    toggleMailModal: {
      type: Function,
    },
    alreadySent: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      mail: {
        to: [],
        cc: [],
        bcc: [],
        subject: "",
        body: "",
        invoicing_date: "",
        sendMeCopy: false,
        from: "",
      },
      user: null,
      submitting: false,
      invoiceNumberPending: false,
      invoiceNumberPlaceholder: INVOICE_NUMBER_PLACEHOLDER,
      formErrors: {},
      contactsList: {},
      loadingContacts: false,
      flatpickrConfig: {
        allowInput: true,
        dateFormat: "Y-m-d",
        locale: French,
      },
    };
  },

  computed: {
    canSubmit() {
      return (
        this.mail.to &&
        this.mail.to.length > 0 &&
        this.mail.subject &&
        this.mail.body &&
        this.mail.invoicing_date
      );
    },
    submitLabel() {
      return this.alreadySent
        ? this.$t("SALES_ORDERS.RESEND_INVOICE")
        : this.$t("COMMON.SEND");
    },

    /**
     * L'opératrice a effacé le jeton de l'objet ET du message : le numéro
     * de facture n'apparaîtra nulle part dans le courriel (le PDF joint le
     * porte toujours). On l'avertit avant l'envoi.
     */
    invoiceNumberPlaceholderRemoved() {
      if (!this.invoiceNumberPending) {
        return false;
      }
      return ![this.mail.subject, this.mail.body].some((text) =>
        (text || "").includes(INVOICE_NUMBER_PLACEHOLDER)
      );
    },
  },

  mounted() {
    this.initialize();
  },

  methods: {
    async initialize() {
      await this.loadUser();
      this.prefillInvoicingDate();
      this.prefillRecipients();
      await this.loadEmailTemplate();
      this.getEstablishmentContacts();
    },

    async loadUser() {
      await this.$store.dispatch("profile/me");
      this.user = this.$store.getters["profile/me"];
      if (this.user) {
        this.mail.from = `${this.user.firstname} ${this.user.lastname} <${this.user.email}>`;
      }
    },

    prefillInvoicingDate() {
      if (!this.salesOrder.event_date) {
        this.mail.invoicing_date = "";
        return;
      }
      // Convert the ISO UTC event_date to the business timezone civil date
      // (America/Toronto). Using moment-timezone ensures the right Y-M-D
      // regardless of the browser's local offset or daylight-saving state.
      try {
        this.mail.invoicing_date = moment
          .tz(this.salesOrder.event_date, BUSINESS_TIMEZONE)
          .format("YYYY-MM-DD");
      } catch (e) {
        this.mail.invoicing_date = String(this.salesOrder.event_date).slice(
          0,
          10
        );
      }
    },

    prefillRecipients() {
      const sentTo = this.salesOrder.sent_to;
      if (sentTo && typeof sentTo === "object") {
        if (Array.isArray(sentTo.to)) {
          this.mail.to = [...sentTo.to];
        }
        if (Array.isArray(sentTo.cc)) {
          this.mail.cc = [...sentTo.cc];
        }
        if (Array.isArray(sentTo.bcc)) {
          this.mail.bcc = [...sentTo.bcc];
        }
      }
      // NOTE: we intentionally do NOT fall back to salesOrder.billing_email.
      // For subsidized orders the billing address (the funder) often differs
      // from the operational recipient of the invoice email, so a silent
      // fallback would risk sending to the wrong party. The operator must
      // pick recipients explicitly on the first send.
      //
      // Exception : sur une soumission de coupons, le client est choisi
      // explicitement à la création du document, donc son courriel EST le
      // destinataire attendu — on le préremplit, l'opérateur peut le changer.
      if (
        !this.mail.to.length &&
        this.salesOrder.pricing_type === PRICING_TYPE_COUPONS &&
        this.salesOrder.billing_email
      ) {
        this.mail.to = [this.salesOrder.billing_email];
      }
    },

    async loadEmailTemplate() {
      try {
        const tpl = await this.$store.dispatch(
          "salesOrders/getEventInvoiceEmailTemplate",
          this.salesOrder.id
        );
        this.mail.subject = tpl.subject || "";
        this.mail.body = tpl.body || "";
        // Jeton présent = la facture n'est pas encore créée : on explique à
        // l'opératrice que le numéro sera inséré à l'envoi.
        this.invoiceNumberPending = [this.mail.subject, this.mail.body].some(
          (text) => text.includes(INVOICE_NUMBER_PLACEHOLDER)
        );
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },

    async getEstablishmentContacts(query = null) {
      return this.getContacts(query, this.salesOrder.establishment);
    },

    async getContacts(query = null, establishment = null) {
      try {
        this.loadingContacts = true;
        let params = {
          sort: "firstname",
          filter: {
            ...(query ? { search: encodeURIComponent(query) } : {}),
            ...(this.salesOrder.organization &&
            this.salesOrder.organization.id
              ? { organization: this.salesOrder.organization.id }
              : {}),
            ...(establishment
              ? {
                  contactableType: "App\\Models\\Establishment",
                  contactableId: establishment.id,
                }
              : {}),
          },
          page: { number: 1, size: 10 },
          include: "contactable",
        };
        await this.$store.dispatch("contacts/list", params);
        const contactsArr = this.$store.getters["contacts/list"];
        this.contactsList = {};
        contactsArr.forEach((contact) => {
          this.contactsList[contact.id] = contact;
        });
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      } finally {
        this.loadingContacts = false;
      }
    },

    async handleSubmit() {
      this.submitting = true;
      const payload = cloneDeep(this.mail);
      // Drop "from" (read-only display only) before sending.
      delete payload.from;
      if (!payload.cc || payload.cc.length === 0) {
        delete payload.cc;
      }
      if (!payload.bcc || payload.bcc.length === 0) {
        delete payload.bcc;
      }

      try {
        const response = await this.$store.dispatch(
          "salesOrders/generateAndSendSubsidizedInvoice",
          {
            ...payload,
            id: this.salesOrder.id,
          }
        );
        // Le numéro n'existe qu'après l'envoi : on le montre ici pour que
        // l'opératrice sache quelle facture vient de partir.
        const seqNumber = response?.invoice_seq_number;
        this.$notify({
          type: "success",
          message: seqNumber
            ? this.$t("SALES_ORDERS.SUBSIDIZED_INVOICE_SENT_WITH_NUMBER", {
                number: seqNumber,
              })
            : this.$t("SALES_ORDERS.SUBSIDIZED_INVOICE_SENT"),
        });
        // Surface any service-side warnings (multiple invoices exist,
        // invoicing_date ignored on reuse, etc.) so the operator sees them.
        const warnings = response?.warnings ?? [];
        warnings.forEach((code) => {
          this.$notify({
            type: "warning",
            duration: 8000,
            message: this.$te(`SALES_ORDERS.WARNINGS.${code}`)
              ? this.$t(`SALES_ORDERS.WARNINGS.${code}`)
              : code,
          });
        });
        if (this.toggleMailModal) {
          this.toggleMailModal();
        }
        this.$emit("sent");
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
        if (error?.response?.data?.errors) {
          this.formErrors = error.response.data.errors;
        }
      } finally {
        this.submitting = false;
      }
    },
  },

  watch: {
    formErrors(errors) {
      if (errors) {
        this.setApiValidation(errors);
      }
    },
  },
};
</script>
